import React, { useEffect, useMemo, useState } from 'react';

import { Button, Select, Input } from 'components/commons';
import toggleArrow from "assets/icons/commons/toggleArrow.svg";
import copy from "assets/icons/commons/copy.svg";
import type { IFeedFormProps } from './FeedForm/FeedForm';
import Toggle from 'components/Toggle/Toggle';
import { IFeedOptionsFilters, IFeedOptionsFrequency, IFeedOptionsMarkets } from "api/services/feed/types";
import { IFormFeedData } from 'redux/feeds/types';
import {t} from "i18next";
import button from "../commons/Button/Button";
import Tooltip from "../Tooltip/Tooltip";
import {FrequencySelector} from "./FeedForm/FrequencySelector";
import {WarehouseSelector} from "./FeedForm/WarehouseSelector";
import iconStateDisabled from "../../assets/icons/commons/source-state-disabled.svg";
import iconStateSuccess from "../../assets/icons/commons/source-state-active.svg";
import iconStateError from "../../assets/icons/commons/source-state-error.svg";
import {formatDateByLanguage} from "../../utils/dateFormatter/dateFormatter";
import store from "../../redux/store";
import {setTemporaryNotifications} from "../../redux/notifications/actions";
import {defaultNotificationCreator} from "../../templates/Notifications";
import {WarehouseType} from "../../constants/Warehouses/WarehouseType";

type IFormSelectData = Pick<IFeedFormProps, "markets" | "filters" | "frequency">

interface IFeedModalItemProps extends IFormSelectData{
    preExpand?: boolean,
    feedData: IFormFeedData,
    changeFormData: (data: IFormFeedData) => void,
    cancelChanging?: () => void,
    deleteFeedHandler?: (id: number) => void,
    restoreFeedHandler?: (id: number) => void
}

const FeedModalItem: React.FC<IFeedModalItemProps> = ({
    preExpand = false,
    markets,
    filters,
    frequency,
    feedData,
    changeFormData,
    cancelChanging,
    deleteFeedHandler,
    restoreFeedHandler,
    ...props
}) => {
    const [isExpanded, setIsExpanded] = useState(preExpand);
    const [feedDataToChange, setFeedDataToChange] = useState<IFormFeedData>({...feedData});
    const [errors, setErrors] = useState<{[key: string]: string}>({});

    // @ts-ignore
    const fieldsWithErrors = Object.keys(errors).filter((k: string) => errors[k].length > 0)
    const isFormHasErrors = fieldsWithErrors.length > 0
    const state = feedDataToChange.isActive ? (isFormHasErrors ? 'fail' : 'success') : 'disabled'
    const dataSourceStateIconAssets = {
        'disabled': iconStateDisabled,
        'success': iconStateSuccess,
        'fail': iconStateError
    }

    const allProductsSelect = { id: 0, name: (t("All products", {ns: "messages"})) };
    const customProductsFilters = [ allProductsSelect, ...filters ];

    let formats: any = [];

    markets.map((item) => {
        formats.push(({value: item.id, label: item.name}))
    })

    formats = [formats.find((item: { value: string; }) => item.value === "GENERAL")].concat(formats.filter((item: { value: string; }) => item.value != "GENERAL"));


    const ozonMarketId = "4080681414";
    const sberMarketId = "3298989609";

    const toggleExpandHandler = () => {
        setIsExpanded(prev => !prev);
    }

    const saveChangedDataHandler = (e: any, data: IFormFeedData) => {
        e.preventDefault();
        setIsExpanded(false);
        changeFormData(data);

    }

    const changeDataToChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFeedDataToChange(prev => {
            return {
                ...prev,
                [e.target.name]: e.target.value
            }
        })
    }

    const changeCheckboxValueHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log(e.target.value, e.target.name)
        setFeedDataToChange(prev => {
            return {
                ...prev,
                [e.target.name]: !(prev as any)[e.target.name]
            }
        })
    }

    const changeActiveFeed = (data: boolean) => {
        feedDataToChange.isActive = data
        setFeedDataToChange({...feedDataToChange})

        if (!isExpanded) {
            changeFormData(feedDataToChange);
        }
    }

    const changeFormatHandler = (data: IFeedOptionsMarkets) => {
        setFeedDataToChange(prev => {
            return {
                ...prev,
                marketId: data.value
            }
        })
    }

    const changeFrequencyHandler = (data: number) => {
        setFeedDataToChange(prev => {
            return {
                ...prev,
                frequency: data
            }
        })
    }

    const changeFilterHandler = (data: IFeedOptionsFilters) => {
        setFeedDataToChange(prev => {
            return {
                ...prev,
                filterId: data.id
            }
        })
    }

    const changeStocksWarehouseHandler = (data: string) => {
        setFeedDataToChange(prev => {
            return {
                ...prev,
                stocksSourceWhID: data
            }
        })
    }

    const copyToClipboard = () => {
        navigator.clipboard.writeText(`${feedDataToChange.url}`)
            .then(_ => console.log('copied'))
    }

    const selectedMarketFormat = useMemo(() => {
        const format = feedDataToChange.marketId || 'GENERAL'
        return formats.find((item: { value: string; }) => {
            return item.value === format
        });
    }, [feedDataToChange.marketId]);

    const selectedFilter = useMemo(() => {
        const filterId = feedDataToChange.filterId || 0
        return customProductsFilters.find(item => {
            return item.id === filterId
        } );
    }, [feedDataToChange.filterId]);

    const cancelBtnHandler = cancelChanging ? cancelChanging : () => setIsExpanded(false);

    useEffect(() => {
        setErrors(prev => {
            return {
                ...prev,
                title: !feedDataToChange.title ? 'Обязательное поле' : '',
                frequency: !feedDataToChange.frequency ? 'Обязательное поле' : '',
                stocksSourceWhID: feedDataToChange.isUpdateStocks && !feedDataToChange.stocksSourceWhID ? 'Обязательное поле' : ''
            }
        })
    }, [feedDataToChange])

    function copyProductsToClipboard() {
        const data = {
            title: 'Продукты для копирования не найдены',
            description: "",
            iconType: 'warning'
        }

        const pids = feedData.generatedPIDs
        if (pids) {
            navigator.clipboard.writeText(pids.join("\n"));
            data.title = 'Идентификаторы продуктов скопированы в буфер'
            data.iconType = 'success'
        }

        store.dispatch(setTemporaryNotifications(
            defaultNotificationCreator(data)
        ))
    }

    return (
        <div {...props} data-testid={props["data-testid" as keyof typeof props]  ? props["data-testid" as keyof typeof props] : "form-modal-item"} className='border border-slate-200 p-2 rounded'>
            <form onSubmit={(e) => saveChangedDataHandler(e, feedDataToChange)}>
            <div className='flex justify-between cursor-pointer' onClick={toggleExpandHandler}>
                <div className='flex items-center'>
                    <div className="mr-2 flex items-center">
                        <img src={dataSourceStateIconAssets[state]} alt="Статус" />
                        <span className='ml-2'>{feedDataToChange.title && feedDataToChange.title}</span>
                    </div>
                </div>
                <div className={`flex`}>
                    <div className="flex mr-4">
                        <Toggle className="mr-2" name="isActive" toggleHandler={changeActiveFeed} isChecked={feedDataToChange.isActive} />
                        <div className="text-[#94A3B8] italic text-sm w-20">
                            <div>{feedDataToChange.isActive ? 'Подключен' : 'Отключен'}</div>
                        </div>
                    </div>
                    <div className={`flex`}>
                        {isExpanded ? <img style={{transform: "rotate(180deg)"}} src={toggleArrow} alt="open" /> : <img src={toggleArrow} alt="close" />}
                    </div>
                </div>

            </div>

            {isExpanded && (
            <>
                <div className='px-6 mt-4'>
                    <div className='my-2'>
                        <Input
                            customLabelClassName ='text-sm'
                            placeholder='Введите наименование'
                            changeHandler={changeDataToChangeHandler}
                            customClassName='py-2'
                            value={feedDataToChange.title}
                            name="title"
                            label="Наименование"
                            validationError={errors.title?.length > 0}
                            validationErrorDescription={errors.title}
                        />
                    </div>
                    <div className='my-2'>
                        <label>
                            <div className='font-[600] text-sm mb-1'>Выберите нужный формат (вначале подключите маркетплейс)</div>
                            <Select
                                onChangeHandler={changeFormatHandler}
                                options={formats}
                                selected={selectedMarketFormat}
                            />
                        </label>
                    </div>

                    <div className='my-2'>
                        <label>
                            <div className='font-[600] text-sm mb-1'>Выберите продукты для выгрузки (вначале создайте фильтр)</div>
                            <Select
                                onChangeHandler={changeFilterHandler}
                                options={customProductsFilters}
                                customValue={"id"}
                                customLabel={"name"}
                                selected={selectedFilter}
                            />
                        </label>
                    </div>
                    <div className='my-2'>
                        <FrequencySelector data={feedDataToChange.frequency} onChange={changeFrequencyHandler}></FrequencySelector>
                    </div>
                    <div className='my-2'>
                        {selectedMarketFormat && selectedMarketFormat.value === ozonMarketId &&
                            <Input
                                customLabelClassName ='text-sm'
                                customClassName='py-2'
                                value={feedDataToChange.warehouse}
                                name="warehouse"
                                changeHandler={changeDataToChangeHandler}
                                label="Название склада из кабинете Озон"
                                validationError={errors.frequency?.length > 0}
                                validationErrorDescription={errors.frequency}
                            />
                        }
                    </div>
                    <div className='my-2'>
                        {selectedMarketFormat && selectedMarketFormat.value === sberMarketId &&
                            <Input
                                customLabelClassName ='text-sm'
                                customClassName='py-2'
                                value={feedDataToChange.warehouse}
                                name="warehouse"
                                changeHandler={changeDataToChangeHandler}
                                label="ID Личного кабинета (или ID Склада)"
                            />
                        }
                    </div>
                </div>

                <div className='flex flex-col px-6 mt-4'>

                    {
                        <label className='flex items-center mb-2'>
                            <input
                                onChange={changeCheckboxValueHandler}
                                className='rounded'
                                type="checkbox"
                                value="Характеристики"
                                name="isUpdateAttributes"
                                checked={feedDataToChange.isUpdateAttributes}
                            />
                            <div className='ml-4 text-sm'>Характеристики</div>
                        </label>
                    }
                    <label className='flex items-center mb-2'>
                        <input
                            className='rounded'
                            type="checkbox"
                            value="Цены"
                            name="isUpdatePrices"
                            checked={feedDataToChange.isUpdatePrices}
                            onChange={changeCheckboxValueHandler}
                        />
                        <div className='ml-4 text-sm'>Цены</div>
                    </label>
                    <div className='flex items-center mb-2'>
                        <label className='flex items-center '>
                            <input
                                onChange={changeCheckboxValueHandler}
                                className='rounded'
                                type="checkbox"
                                value="Остатки"
                                name="isUpdateStocks"
                                checked={feedDataToChange.isUpdateStocks}
                            />
                            <div className='ml-4 text-sm'>Остатки</div>
                        </label>
                        <Tooltip title={'Выберите склад с которого будут браться остатки'}>
                            <svg className="ml-3 w-4 h-4 fill-current text-gray-400" viewBox="0 0 16 16">
                                <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
                            </svg>
                        </Tooltip>
                    </div>

                    {feedDataToChange.isUpdateStocks &&
                        <WarehouseSelector
                            data={feedDataToChange.stocksSourceWhID}
                            onChange={changeStocksWarehouseHandler}
                            validationError={errors.stocksSourceWhID?.length > 0}
                            validationErrorDescription={errors.stocksSourceWhID}
                            filterFunc={(v:any) => v.status === 'active' && (v.isEnableExport || v.type === WarehouseType.ExternalWarehouseFBO)}
                        ></WarehouseSelector>
                    }
                </div>
            </>
            )}

            <div className='px-6 mt-4'>
                <div className='flex items-center justify-between'>
                    {feedDataToChange.url &&
                        <div>
                            <div className='flex items-center'>
                                <button type="button" onClick={copyToClipboard}>
                                    <img className='mr-2' src={copy} alt='copy' />
                                </button>
                                <a className='text-[#2255A1] text-sm' style={{maxWidth: "100%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"}} target={"_blank"} title={feedDataToChange.url} href={feedDataToChange.url}>{feedDataToChange.url}</a>
                            </div>
                            <div className='flex mt-4'>
                                <div className='text-sm flex flex-row'>
                                    <span className='text-[#2255A1] pr-1'>{feedData.generatedPIDs.length}</span> продуктов
                                    <button className="text-[#2255A1] ml-1" type="button" onClick={copyProductsToClipboard}>скопировать в буфер</button>.
                                </div>
                                <div className='text-sm ml-1'>Обновлено: {formatDateByLanguage(new Date(feedData.generatedAt.replace(/-/g, "/")), "Pp")}</div>
                            </div>
                        </div>
                    }
                    {feedDataToChange.id && !feedDataToChange.url &&
                        <div className='flex items-center text-sm'>В обработке</div>
                    }
                </div>
            </div>

            {
                isExpanded &&
                <div style={{borderTop: "1px solid #eee"}} className='px-6 mt-4 pt-[10px]'>
                    <div className='flex items-center justify-end'>
                        <div className='flex justify-between w-full'>
                            <div className='flex'>
                                <div className='mr-4'>
                                    <Button
                                        type="submit"
                                        appearance="primary"
                                        // data-testid={"change-btn-form-feed"}
                                        // clickHandler={() => saveChangedDataHandler(feedDataToChange)}
                                        disabled={isFormHasErrors}
                                    >Сохранить</Button>
                                </div>
                                <div>
                                    <Button type="button" data-testid="cancel-btn-form-feed" clickHandler={cancelBtnHandler} appearance='secondary'>Отмена</Button>
                                </div>
                            </div>
                            <div>
                                {feedData.isDeleted === false && deleteFeedHandler && typeof feedDataToChange.id === "number" &&
                                    <Button type="button" clickHandler={() => deleteFeedHandler(feedDataToChange.id as number)} appearance="danger"
                                    >В архив</Button>
                                }
                                {feedData.isDeleted && restoreFeedHandler && typeof feedDataToChange.id === "number" &&
                                    <Button type="button" clickHandler={() => restoreFeedHandler(feedDataToChange.id as number)} appearance="success">Восстановить</Button>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            }
            </form>
        </div>
    )
}

export default FeedModalItem;